<template>
  <div class="custom-cross-words-wrap" id="custom-cross-words-wrap">
    <div class="customization-title">Enter Names For Crossword *</div>
    <div class="custom-cross-words__input-wrap">
      <WordsInput
        class="custom-cross-words__input"
        :tags="words"
        placeholder="Insert words"
        @on-tags-changed="handleChangeWord"
        :read-only="loading"
        :loading="loading"
      >
      </WordsInput>
      <div class="custom-cross-words__input-loading lds-ring" v-show="loading">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>

    <div
      class="custom-cross-words__explain"
      v-if="errorCrossWords && !loading && words.length > 1"
    >
      No letters in the word '{{ words[words.length - 1] }}' exist in any other
      word
    </div>
    <div class="custom-cross-words__explain" v-if="isTyped && words.length < 2">
      We need at least two words to generate a puzzle image
    </div>
    <div class="custom-cross-words__guide">
      Insert words to create your puzzle. Please only use alphabet characters
      (a-z), DO NOT use numbers and special characters. <br />
      Separate each name with a space or by pressing Enter.
    </div>
    <div class="custom-cross-words__image-wrap">
      <div
        class="custom-cross-words__image"
        v-if="wordsBoard"
        ref="customCrossWords"
      >
        <div
          class="custom-cross-words"
          :style="`gap: ${customizationTemplate.margin || '1rem'}`"
        >
          <div
            v-for="(row, i) in wordsBoard.width > wordsBoard.height
              ? wordsBoard.width
              : wordsBoard.height"
            :key="i"
            class="custom-cross-words__row"
            :style="`gap: ${customizationTemplate.margin || '1rem'}`"
          >
            <template
              v-for="(column, j) in wordsBoard.width > wordsBoard.height
                ? wordsBoard.width
                : wordsBoard.height"
              :key="j"
            >
              <div
                :class="fillWord(i, j) ? '' : 'visibility'"
                class="custom-cross-words__column"
                :style="`background: url(${
                  customizationTemplate.backgroundImage || 'unset'
                });
                font-family: ${
                  customizationTemplate.fontFamilyName
                    ? customizationTemplate.fontFamilyName
                    : customizationTemplate.fontFamilyLink
                    ? 'FontFamilyCrossWords'
                    : 'unset'
                };
                color: ${customizationTemplate.color || 'unset'};
                font-size: ${customizationTemplate.fontSize || '40px'}`"
              >
                {{ fillWord(i, j) }}
                <span class="number-word">{{ fillNumberWord(i, j) }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import html2canvas from 'html2canvas'
import { ref, watch, onMounted } from 'vue'
import { cloneDeep, REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR } from '@/utils'
import Provider from '@/provider'
import WordsInput from 'vue3-tags-input'
import { useProductCustomization } from './composables/ProductCustomization'

const props = withDefaults(
  defineProps<{
    inputFileCustomily: Element
    customizationTemplate: {
      backgroundImage?: string
      fontFamilyLink?: string
      fontFamilyName?: string
      color?: string
      margin?: string
      fontSize?: string
    }
    customilyOptionWrapper: HTMLElement
  }>(),
  {}
)

const { convertImageLinkToFileList, changeFileImageCustomily } =
  useProductCustomization()

const wordsBoard = ref<any>('')
const customCrossWords = ref(null)
const words = ref<any>([])
const isTyped = ref(false)
const loading = ref(false)
const errorCrossWords = ref(false)

onMounted(() => {
  if (props.customizationTemplate.fontFamilyLink) {
    const fontFile = new FontFace(
      'FontFamilyCrossWords',
      `url(${props.customizationTemplate.fontFamilyLink})`
    )
    document.fonts.add(fontFile)
    fontFile?.load()
  }
})

watch(
  () => words.value,
  () => {
    isTyped.value = true
  },
  { deep: true }
)

async function handleChangeWord(tags: string[]) {
  words.value = tags
  if (words.value.length > 1) {
    try {
      loading.value = true
      errorCrossWords.value = false

      await crossWordsCustomPublic(words.value)
      await captureImage()
      loading.value = false
    } catch (error) {
      loading.value = false
      errorCrossWords.value = true
    }
  } else {
    const removeImageButtonCustomily =
      props.customilyOptionWrapper?.querySelector(
        REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR
      )
    if (removeImageButtonCustomily) removeImageButtonCustomily.click()
  }
}

async function captureImage() {
  if (!customCrossWords.value) return
  html2canvas(customCrossWords.value, {
    useCORS: true,
    allowTaint: false,
    backgroundColor: null,
  }).then(async (canvas: any) => {
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    const imageFileList = await convertImageLinkToFileList(
      link.href,
      'cross-word'
    )
    changeFileImageCustomily(imageFileList, props.inputFileCustomily)
  })
}
async function crossWordsCustomPublic(words: any) {
  try {
    const provider = await Provider.getInstance()
    const dataCrossWord = await provider.crossWordsCustomPublic(words)
    wordsBoard.value = dataCrossWord.value.board
  } catch (error) {
    wordsBoard.value = null
    errorCrossWords.value = true
  }
}

function fillWord(y: number, x: number) {
  const wordsBoardClone = cloneDeep(wordsBoard.value)
  return wordsBoardClone.cells.find((item: any) => item.x == x && item.y == y)
    ?.v
}
function fillNumberWord(y: number, x: number) {
  const wordsBoardClone = cloneDeep(wordsBoard.value)
  return wordsBoardClone.cells.find((item: any) => item.x == x && item.y == y)
    ?.m
}
</script>

<style lang="scss">
.custom-cross-words {
  display: grid;
  &__input {
    border: 1px solid var(--border-color);
    padding-left: 15px !important;
    line-height: 25px;
  }

  &__image {
    &-wrap {
      position: absolute;
      z-index: -5;
      left: -5000px;
      opacity: 0;
    }
    padding: 1.5rem;
    width: fit-content;
  }
  &__row {
    display: flex;
  }
  &__column {
    position: relative;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
  }
  &__guide {
    margin-top: 0.5rem;
    color: #999;
    white-space: pre-wrap;
    font-style: italic;
    font-size: 11px;
  }
  &__explain {
    margin-top: 0.5rem;
    color: var(--color-secondary);
    font-size: 11px;
  }
  .visibility {
    opacity: 0;
  }
  &__input-wrap {
    position: relative;
    .lds-ring {
      position: absolute;
      right: 15px;
      top: 15px;
      --loading-size: 15px;
      --loading-line-width: 2px;
      color: #999999;
    }
  }
  .word {
    line-height: 1;
  }
  .number-word {
    position: absolute;
    right: 4px;
    bottom: 3px;
    font-size: 9px;
  }
}

.sib-position-custom-personalization #custom-cross-words-wrap {
  .v3ti .v3ti-tag {
    background-color: #fafafa !important;
    margin: 8px 8px 0 0px !important;
    color: #000000a6 !important;
    border: 1px solid #e8e8e8;
  }
  .v3ti--focus {
    outline: 2px !important;
    box-shadow: 0 0 0 2px #000000 !important;
  }
  .v3ti-content input.v3ti-new-tag {
    border: 0px !important;
    margin: 0;
    min-height: 42px !important;
    min-width: unset !important;
    height: unset !important;
    padding: unset !important;
  }
  .v3ti .v3ti-tag .v3ti-remove-tag {
    padding: 0 0 0 6px;
    color: #000000a6 !important;
  }
}
</style>
